import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { useRegisterDelegate } from "../../hooks/useRegisterDelegate"; 

const FinishRegistration = ({ mySubscription }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const formDataFromPayment = state?.formData || {};
  const userId = state?.userId || formDataFromPayment._id; // Assume ID is passed from previous step

  const [formData, setFormData] = useState({
    roleInConference: "",
    requestInvitationLetter: "No",
    sessionInterests: [],
    arrivalDate: "",
    departureDate: "",
    flightNumber: "",
    photo: null,
    shareContactDetails: "No",
    agreeToTerms: false,
    passportNumber: formDataFromPayment.passportNumber || "",
  });

  const [errors, setErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);

  const { loading, success, error, message, updateDelegate } =
    useRegisterDelegate();

  const roles = ["Speaker", "Participant", "Organizer", "Media", "Sponsor"];
  const sessionOptions = [
    "Technology Innovations",
    "Sustainability",
    "Networking Events",
    "Workshops",
    "Keynote Speeches",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleMultiSelectChange = (e) => {
    const options = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    setFormData({ ...formData, sessionInterests: options });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, photo: file });
    if (file) {
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // if (!formData.roleInConference)
    //   newErrors.roleInConference = "Role is required";
    // if (!formData.requestInvitationLetter)
    //   newErrors.requestInvitationLetter = "Please select an option";
    if (formData.sessionInterests.length === 0)
      newErrors.sessionInterests = "Select at least one session";
    if (!formData.agreeToTerms)
      newErrors.agreeToTerms = "You must agree to the terms";
    if (!formData.passportNumber)
      newErrors.passportNumber = "Passport or National ID is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // if (!userId) {
    //   Swal.fire({
    //     title: "Error!",
    //     text: "User ID not found. Please start the registration process again.",
    //     icon: "error",
    //     confirmButtonText: "OK",
    //     confirmButtonColor: "#ef4444",
    //   });
    //   return;
    // }

    if (validateForm()) {
      // formData.photo = null;
      await updateDelegate(formData);

      // Handle success/error in useEffect
    } else {
      Swal.fire({
        title: "Error!",
        text: "Please complete all required fields.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  useEffect(() => {
    if (success && message) {
      Swal.fire({
        title: "Registration Completed!",
        text: message,
        icon: "success",
        confirmButtonText: "Go to Tickets",
        confirmButtonColor: "#059669",
      }).then(() => {
        const completeData = { ...formDataFromPayment, ...formData };
        navigate("/ticket", { state: { formData: completeData } });
      });
    } else if (error) {
      Swal.fire({
        title: "Error!",
        text: error,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    }
  }, [success, error, message, navigate, formDataFromPayment, formData]);

  return (
    <div className="min-h-screen  flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-4xl bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl overflow-hidden border border-green-200"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-500 p-6 sm:p-8 text-center text-white relative overflow-hidden">
          <motion.h1
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold"
          >
            Finish Your Registration
          </motion.h1>
          <motion.p
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-3 text-lg sm:text-xl text-green-100"
          >
            Just a few more steps to join the excitement!
          </motion.p>
          <div className="absolute top-0 left-0 w-24 h-24 bg-green-400 rounded-full -ml-12 -mt-12 opacity-30 animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-teal-400 rounded-full -mr-16 -mb-16 opacity-30 animate-pulse"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 lg:p-10 space-y-8">
          {/* Participation Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-md border border-green-100"
          >
            <h2 className="text-2xl font-semibold text-green-600 mb-6">
              4. Participation Details
            </h2>
            <div className="space-y-6">
              {/* 4.1 Role in Conference */}
              {/* <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role in the Conference <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-500 bg-white hover:bg-gray-50 shadow-sm transition-all appearance-none text-gray-600"
                    name="roleInConference"
                    value={formData.roleInConference}
                    onChange={handleChange}
                  >
                    <option value="">Choose your role</option>
                    {roles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-teal-500 pointer-events-none">
                    ▼
                  </span>
                </div>
                {errors.roleInConference && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.roleInConference}
                  </p>
                )}
              </div> */}

              {/* 4.2 Request Invitation Letter */}
              {/* <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Request Official Invitation Letter?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {["Yes", "No"].map((option) => (
                    <label
                      key={option}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="requestInvitationLetter"
                        value={option}
                        checked={formData.requestInvitationLetter === option}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <span
                        className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center transition-all ${
                          formData.requestInvitationLetter === option
                            ? "border-teal-500 bg-teal-500"
                            : "border-gray-300 hover:border-teal-400"
                        }`}
                      >
                        {formData.requestInvitationLetter === option && (
                          <span className="w-2.5 h-2.5 bg-white rounded-full"></span>
                        )}
                      </span>
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                {errors.requestInvitationLetter && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.requestInvitationLetter}
                  </p>
                )}
              </div> */}

              {/* Passport or National ID Number */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passport or National ID Number{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-500 bg-white hover:bg-gray-50 shadow-sm transition-all"
                  type="text"
                  name="passportNumber"
                  value={formData.passportNumber}
                  onChange={handleChange}
                  placeholder="Enter your ID"
                />
                {errors.passportNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.passportNumber}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Additional Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-md border border-green-100"
          >
            <h2 className="text-2xl font-semibold text-green-600 mb-6">
              6. Additional Information
            </h2>
            <div className="space-y-6">
              {/* 6.1 Session Interests */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Interests <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    multiple
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-500 bg-white hover:bg-gray-50 shadow-sm transition-all"
                    name="sessionInterests"
                    value={formData.sessionInterests}
                    onChange={handleMultiSelectChange}
                  >
                    {sessionOptions.map((session) => (
                      <option
                        key={session}
                        value={session}
                        className="p-2 hover:bg-teal-50"
                      >
                        {session}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple
                </p>
                {errors.sessionInterests && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.sessionInterests}
                  </p>
                )}
              </div>

              {/* 6.2 Accommodation & Travel */}
              {mySubscription == "INTERNATIONAL" && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700">
                    Accommodation & Travel{" "}
                    <span className="text-gray-500 italic">
                      (Optional - Submit later if needed)
                    </span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="block text-sm text-gray-600 mb-1">
                        Arrival Date
                      </label>
                      <input
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-500 bg-white hover:bg-gray-50 shadow-sm transition-all"
                        type="date"
                        name="arrivalDate"
                        value={formData.arrivalDate}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="relative">
                      <label className="block text-sm text-gray-600 mb-1">
                        Departure Date
                      </label>
                      <input
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-500 bg-white hover:bg-gray-50 shadow-sm transition-all"
                        type="date"
                        name="departureDate"
                        value={formData.departureDate}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="relative sm:col-span-2">
                      <label className="block text-sm text-gray-600 mb-1">
                        Flight Number
                      </label>
                      <input
                        className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-400 focus:border-teal-500 bg-white hover:bg-gray-50 shadow-sm transition-all"
                        type="text"
                        name="flightNumber"
                        value={formData.flightNumber}
                        onChange={handleChange}
                        placeholder="e.g., ET123"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 6.3 Upload Photo */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Photo{" "}
                  <span className="text-gray-500 italic">
                    (For badge - Optional)
                  </span>
                </label>
                <input
                  className="w-full p-3 rounded-lg border border-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-100 file:text-teal-700 hover:file:bg-teal-200 transition-all bg-white shadow-sm"
                  type="file"
                  name="photo"
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {photoPreview && (
                  <div className="mt-4 relative group">
                    <img
                      src={photoPreview}
                      alt="Photo Preview"
                      className="w-32 h-32 object-cover rounded-full border-2 border-teal-200 shadow-md mx-auto"
                    />
                    <button
                      onClick={() => {
                        setPhotoPreview(null);
                        setFormData({ ...formData, photo: null });
                      }}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Consent & Confirmation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-md border border-green-100"
          >
            <h2 className="text-2xl font-semibold text-green-600 mb-6">
              7. Consent & Confirmation
            </h2>
            <div className="space-y-6">
              {/* 7.1 Share Contact Details */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share contact details with participants?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {["Yes", "No"].map((option) => (
                    <label
                      key={option}
                      className="flex items-center cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="shareContactDetails"
                        value={option}
                        checked={formData.shareContactDetails === option}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <span
                        className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center transition-all ${
                          formData.shareContactDetails === option
                            ? "border-teal-500 bg-teal-500"
                            : "border-gray-300 hover:border-teal-400"
                        }`}
                      >
                        {formData.shareContactDetails === option && (
                          <span className="w-2.5 h-2.5 bg-white rounded-full"></span>
                        )}
                      </span>
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 7.2 Agree to Terms */}
              <div className="relative">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    className="hidden"
                  />
                  <span
                    className={`w-5 h-5 rounded-md border-2 mr-2 flex items-center justify-center transition-all ${
                      formData.agreeToTerms
                        ? "border-teal-500 bg-teal-500"
                        : "border-gray-300 hover:border-teal-400"
                    }`}
                  >
                    {formData.agreeToTerms && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="text-sm text-gray-700">
                    I agree to the{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      className="text-teal-600 hover:underline"
                    >
                      conference terms and conditions
                    </a>{" "}
                    <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.agreeToTerms && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.agreeToTerms}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <motion.button
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={loading}
              className={`text-base font-bold tracking-wide bg-gradient-to-r from-green-600 to-teal-500 text-white p-4 rounded-full w-full md:w-2/3 shadow-lg hover:from-green-700 hover:to-teal-600 transition-all focus:outline-none focus:ring-4 focus:ring-teal-300 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Submitting..." : "Complete Registration"}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default FinishRegistration;
